import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/stores/authStore";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2, Mail, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EmailVerificationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [error, setError] = useState("");

  useEffect(() => {
    if (token) {
      verifyEmail(token);
    } else {
      setStatus("error");
      setError("Invalid verification link");
    }
  }, [token]);

  const verifyEmail = async (verificationToken) => {
    try {
      await axios.get(`${API}/auth/verify-email?token=${verificationToken}`);
      setStatus("success");
      toast.success("Email verified successfully!");
    } catch (error) {
      setStatus("error");
      setError(error.response?.data?.detail || "Verification failed. The link may be expired or invalid.");
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
      <Card className="max-w-md w-full bg-[#09090b] border-white/10">
        <CardContent className="p-8 text-center">
          {status === "verifying" && (
            <>
              <Loader2 className="w-16 h-16 text-[#00f0ff] mx-auto mb-4 animate-spin" />
              <h1 className="text-2xl font-bold text-white mb-2">Verifying Email</h1>
              <p className="text-zinc-500">Please wait while we verify your email address...</p>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircle className="w-16 h-16 text-[#00ff9d] mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2">Email Verified!</h1>
              <p className="text-zinc-500 mb-6">
                Your email has been successfully verified. You now have full access to ZeroZone.
              </p>
              <Button
                onClick={() => navigate("/")}
                className="bg-[#00f0ff] text-black hover:bg-[#00f0ff]/90"
              >
                Go to Dashboard
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2">Verification Failed</h1>
              <p className="text-zinc-500 mb-6">{error}</p>
              <div className="flex gap-3 justify-center">
                <Button
                  onClick={() => navigate("/")}
                  variant="outline"
                  className="border-white/10 text-white hover:bg-white/5"
                >
                  Go Home
                </Button>
                <Button
                  onClick={() => navigate("/resend-verification")}
                  className="bg-[#00f0ff] text-black hover:bg-[#00f0ff]/90"
                >
                  Resend Email
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const EmailVerificationBanner = ({ className = "" }) => {
  const { user, token } = useAuth();
  const [resending, setResending] = useState(false);

  // Don't show if user is verified or not logged in
  if (!user || user.email_verified !== false) {
    return null;
  }

  const resendVerification = async () => {
    try {
      setResending(true);
      await axios.post(`${API}/auth/resend-verification`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Verification email sent! Check your inbox.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send verification email");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className={`bg-yellow-500/10 border border-yellow-500/30 rounded-sm p-4 ${className}`}>
      <div className="flex items-center gap-4">
        <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm text-yellow-200">
            Please verify your email address to access all features.
          </p>
        </div>
        <Button
          onClick={resendVerification}
          disabled={resending}
          size="sm"
          className="bg-yellow-500/20 text-yellow-200 hover:bg-yellow-500/30 border border-yellow-500/30"
        >
          {resending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Mail className="w-4 h-4 mr-2" />
              Resend
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
